import { OfflineErrorCode, OfflineViewState, View } from "@novorender/api";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { dataApi } from "apis/dataV1";
import { useLazyGetBookmarksQuery, useLazyGetGroupIdsQuery } from "apis/dataV2/dataV2Api";
import { buildModelRevisionsArgs } from "apis/procore/modelRevisions";
import { useLazyGetModelRevisionsQuery, useLazyGetModelsQuery } from "apis/procore/procoreApi";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { StorageKey } from "config/storage";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { useAbortController } from "hooks/useAbortController";
import { useSceneId } from "hooks/useSceneId";
import { selectUser } from "slices/authSlice";
import {
    selectBackendProvider,
    selectIncrementalCachingDisabled,
    selectIsProcoreDesign,
    selectProcoreContext,
    selectProjectV2Info,
} from "slices/explorer";
import { telemetry } from "telemetry";
import type { OfflineDownloadErrorCode, OfflineDownloadStatus, OfflineDownloadStatusPayload } from "types/events";
import { isPhone, isTablet } from "utils/misc";
import { getFromStorage } from "utils/storage";

import { offlineActions, selectIncrementalStorageLimit, selectOfflineAction } from "./offlineSlice";

type OfflineSceneMeta = {
    id: string;
    name: string;
    lastSynced: string;
    viewerScenes: { name: string; id: string; lastSynced: string }[];
};

export const GB = 1000 * 1000 * 1000;
const DEFAULT_INCREMENTAL_STORAGE_LIMIT = isPhone ? 5 * GB : isTablet ? 10 * GB : 50 * GB;

const offlineSceneStorageKey = (parentSceneId: string) => `offline_scene/${parentSceneId}`;
const storage = {
    get: (parentSceneId: string): OfflineSceneMeta | undefined => {
        const stored = localStorage.getItem(offlineSceneStorageKey(parentSceneId));

        if (!stored) {
            return;
        }

        try {
            const scene = JSON.parse(stored) as OfflineSceneMeta;
            return scene;
        } catch (e) {
            console.warn(e);
            telemetry.trackCaughtException({
                source: "offline.useHandleOffline.getScene",
                category: "data",
                error: e,
            });
            localStorage.removeItem(offlineSceneStorageKey(parentSceneId));
        }
    },
    set: (scene: OfflineSceneMeta): void => {
        localStorage.setItem(offlineSceneStorageKey(scene.id), JSON.stringify(scene));
    },
    remove: (parentSceneId: string): void => {
        localStorage.removeItem(offlineSceneStorageKey(parentSceneId));
    },
};

export function getOfflineDownloadErrorMessage(
    errorId: OfflineErrorCode | undefined,
    translate: (key: "notEnoughSpaceError" | "networkDownloadError" | "genericDownloadError") => string,
): string {
    switch (errorId) {
        case OfflineErrorCode.quotaExceeded:
            return translate("notEnoughSpaceError");
        case OfflineErrorCode.offline:
            return translate("networkDownloadError");
        default:
            return translate("genericDownloadError");
    }
}

/**
 * The SDK statuses forwarded to the host as they arrive.
 *
 * Failures are absent on purpose: the SDK pairs every failing status with an `error` callback, so
 * they reach the host from there as one `"error"` event that carries an error code. `"deleting"` is
 * absent too — the SDK uses it for both a scene delete and stale-file pruning, so the delete action
 * reports its own. Statuses a future SDK adds are dropped rather than forwarded unrecognized.
 */
const HOST_REPORTED_SDK_STATUSES = new Set<string>([
    "scanning",
    "synchronizing",
    "synchronized",
    "aborted",
] satisfies OfflineDownloadStatus[]);

function isHostReportedStatus(status: string): status is OfflineDownloadStatus {
    return HOST_REPORTED_SDK_STATUSES.has(status);
}

/** Locale-independent counterpart of {@link getOfflineDownloadErrorMessage}, for the host. */
export function getOfflineDownloadErrorCode(errorId: OfflineErrorCode | undefined): OfflineDownloadErrorCode {
    switch (errorId) {
        case OfflineErrorCode.quotaExceeded:
            return "quotaExceeded";
        case OfflineErrorCode.offline:
            return "offline";
        case OfflineErrorCode.downloadFailed:
            return "downloadFailed";
        default:
            return "unknown";
    }
}

export function useHandleOffline() {
    const viewerSceneId = useSceneId();
    const {
        state: { offlineWorkerState, view },
    } = useExplorerGlobals();
    const user = useAppSelector(selectUser);

    const action = useAppSelector(selectOfflineAction);
    const [abortController, abort] = useAbortController();
    const [_deleteStaleFilesAbortController, abortDeleteStaleFiles] = useAbortController();
    const createLogger = useCreateLogger();
    const emitDownloadStatus = useEmitDownloadStatus();
    const dispatch = useAppDispatch();
    const [getBookmarks] = useLazyGetBookmarksQuery();
    const [getGroupIds] = useLazyGetGroupIdsQuery();
    const projectId = useAppSelector(selectProjectV2Info)?.id;
    const incrementalStorageLimit = useAppSelector(selectIncrementalStorageLimit);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const incrementalCachingDisabled = useAppSelector(selectIncrementalCachingDisabled);
    const isProcoreBackendProvider = useAppSelector(selectBackendProvider) === "procore";
    const getProcoreModelTitle = useGetProcoreModelTitle();

    useEffect(
        function initOfflineScenes() {
            if (!offlineWorkerState || !projectId || !view) {
                return;
            }
            let mounted = true;

            let initIncrementalStorageLimit = Number(getFromStorage(StorageKey.IncrementalOfflineLimit));
            if (!initIncrementalStorageLimit) {
                initIncrementalStorageLimit = DEFAULT_INCREMENTAL_STORAGE_LIMIT;
            }
            dispatch(offlineActions.setIncrementalStorageLimit(initIncrementalStorageLimit));

            offlineWorkerState.onIncrementalSizeChange = (size) => {
                dispatch(offlineActions.setIncrementalStorageSize(size));
            };

            offlineWorkerState.onIncrementalSceneRemoved = (sceneId) => {
                storage.remove(sceneId);
                dispatch(offlineActions.removeScene(sceneId));
            };

            // initEvictionLoop can potentially hang forever if another tab is
            // managing eviction right now and holding the lock
            offlineWorkerState
                .initEvictionLoop()
                .then(() => {
                    if (!mounted) {
                        return;
                    }
                    const thirtyDaysAgo = Date.now() - 1000 * 60 * 60 * 24 * 30;
                    offlineWorkerState.removeStaleIncrementalScenes(thirtyDaysAgo, [projectId]);
                })
                .catch((e) => {
                    console.error("Error initializing eviction loop:", e);
                    telemetry.trackCaughtException({
                        source: "offline.useHandleOffline.initEvictionLoop",
                        category: "data",
                        error: e,
                        severity: "error",
                    });
                });

            [...offlineWorkerState.scenes.values()].forEach((scene) => {
                const meta = storage.get(scene.id);

                if (!meta) {
                    return;
                }

                const isIncremental = scene.manifest.numFiles === 0;

                dispatch(
                    offlineActions.addScene({
                        id: scene.id,
                        name: meta.name,
                        status: isIncremental ? "incremental" : "synchronized",
                        lastSync: meta.lastSynced,
                        progress: "",
                        scanProgress: "",
                        size: scene.manifest.totalByteSize,
                        viewerScenes: meta.viewerScenes,
                    }),
                );

                // TODO(NSO-386): Uncomment when NSO-386 is fixed (restore incremental autoclean + `deleteStaleFiles` abort on scene delete).
                // if (isIncremental && scene.id === projectId) {
                //     // Clean up stale files for the current project in incremental mode
                //     console.log("Delete stale offline files");
                //     offlineWorkerState.scenes
                //         .get(scene.id)
                //         ?.deleteStaleFiles(getSceneIndexUrl(view), deleteStaleFilesAbortController.current.signal)
                //         .then((result) => {
                //             console.log("Stale files deleted", result);
                //         })
                //         .catch((error) => {
                //             console.error("Error deleting stale files:", error);
                //             telemetry.trackCaughtException({
                //                 source: "offline.useHandleOffline.deleteStaleFiles",
                //                 category: "data",
                //                 error,
                //                 severity: "error",
                //             });
                //         });
                // }

                scene.logger = createLogger(scene.id, isIncremental ? "incremental" : "fullSync");
            });

            // Procore auto-enables incremental on first load (web + mobile),
            // unless the iOS host kill switch (incrementalCachingDisabled) is on.
            // Standalone handles this via IncrementalLoadingPopup. Procore mobile
            // additionally exposes Download for offline (fullSync) in Settings.
            if (isProcoreDesign && !incrementalCachingDisabled && !offlineWorkerState.scenes.has(projectId)) {
                dispatch(offlineActions.setAction({ action: "incrementalSync" }));
            }

            return () => {
                mounted = false;
                if (offlineWorkerState) {
                    offlineWorkerState.onIncrementalSizeChange = undefined;
                    offlineWorkerState.onIncrementalSceneRemoved = undefined;
                }
            };
        },
        [offlineWorkerState, dispatch, projectId, createLogger, view, isProcoreDesign, incrementalCachingDisabled],
    );

    useEffect(() => {
        if (!offlineWorkerState || incrementalStorageLimit === null) {
            return;
        }
        offlineWorkerState.setIncrementalStorageLimit(incrementalStorageLimit);
    }, [offlineWorkerState, dispatch, incrementalStorageLimit]);

    useEffect(() => {
        handleAction();

        async function handleAction() {
            if (!view || !projectId || !offlineWorkerState || !action) {
                return;
            }

            const parentSceneId = projectId;
            let resetAction = true;

            switch (action.action) {
                case "delete": {
                    abortDeleteStaleFiles();
                    const id = action.id ?? parentSceneId;

                    // The delete reports its own status: the SDK announces completion on the
                    // context logger, which carries no scene id and which the viewer doesn't
                    // install, and the scene's own logger is gone by the time it finishes.
                    emitDownloadStatus("delete", { status: "deleting" });
                    try {
                        await offlineWorkerState.scenes.get(id)?.delete();
                    } catch (e) {
                        // Swallowed for the same reason as the fullSync branch below: a rejection
                        // here escapes a fire-and-forget call, skipping the `action` reset and
                        // leaving the UI stuck mid-delete. The scene stays in the store and in
                        // storage, because its files may still be on disk.
                        telemetry.trackCaughtException({
                            error: e,
                            source: "offline.useHandleOffline.delete",
                            severity: "error",
                            category: "data",
                        });
                        // No `error` message: the viewer's copy is all download-flavoured, and
                        // wrong copy is worse for a host than none.
                        emitDownloadStatus("delete", { status: "error", errorCode: "unknown" });
                        break;
                    }
                    storage.remove(id);
                    dispatch(offlineActions.removeScene(id));
                    emitDownloadStatus("delete", { status: "deleted" });

                    // Re-arm incremental caching: addScene broadcasts "add-scene"
                    // which clears the SDK's offlineDirs null-cache for this id.
                    if (isProcoreDesign && !incrementalCachingDisabled && id === parentSceneId) {
                        resetAction = false;
                        dispatch(offlineActions.setAction({ action: "incrementalSync" }));
                    }

                    break;
                }
                case "pause": {
                    abort();
                    break;
                }
                case "incrementalSync": {
                    // Kill switch enforcement point. Only ever true on Procore iOS,
                    // so standalone opt-in (IncrementalLoadingPopup / offline widget)
                    // is unaffected.
                    if (incrementalCachingDisabled) {
                        break;
                    }

                    const scene =
                        offlineWorkerState.scenes.get(parentSceneId) ??
                        (await offlineWorkerState.addScene(parentSceneId));

                    if (!scene) {
                        break;
                    }

                    if (
                        (await scene.readManifest(getSceneIndexUrl(view), abortController.current.signal)) === undefined
                    ) {
                        dispatch(
                            offlineActions.addScene({
                                id: scene.id,
                                name: scene.id,
                                status: "invalid format",
                                lastSync: new Date().toISOString(),
                                progress: "",
                                scanProgress: "",
                                size: 0,
                                viewerScenes: [],
                            }),
                        );
                        break;
                    }

                    let sceneTitle = "";

                    if (isProcoreBackendProvider) {
                        sceneTitle =
                            (await getProcoreModelTitle(viewerSceneId).catch((err) => {
                                console.error(err);
                                return;
                            })) ?? viewerSceneId;
                    } else {
                        const sceneData = await dataApi.loadScene(viewerSceneId);
                        if ("error" in sceneData) {
                            scene.logger?.error({ message: "Failed to load scene data" });
                            break;
                        }
                        sceneTitle = sceneData.title;

                        await Promise.all([
                            getBookmarks({ projectId: viewerSceneId }).unwrap(),
                            user
                                ? getBookmarks({ projectId: viewerSceneId, personal: true }).unwrap()
                                : Promise.resolve(),
                            ...sceneData.objectGroups
                                .filter((group) => group.id && !group.ids)
                                .map((group) => getGroupIds({ projectId: viewerSceneId, groupId: group.id }).unwrap()),
                        ]);
                    }

                    const persisted = await navigator.storage.persisted();
                    if (!persisted) {
                        await navigator.storage.persist();
                    }

                    const meta = storage.get(parentSceneId);
                    const viewerScene = {
                        id: viewerSceneId,
                        name: sceneTitle,
                        lastSynced: new Date().toISOString(),
                    };

                    const toStore = {
                        id: parentSceneId,
                        name: parentSceneId,
                        lastSynced: meta?.lastSynced ?? "",
                        viewerScenes: meta
                            ? meta.viewerScenes.find((vs) => vs.id === viewerSceneId)
                                ? meta.viewerScenes.map((vs) => (vs.id === viewerSceneId ? viewerScene : vs))
                                : meta.viewerScenes.concat(viewerScene)
                            : [viewerScene],
                    };
                    storage.set(toStore);

                    dispatch(
                        offlineActions.addScene({
                            id: scene.id,
                            name: scene.id,
                            status: "incremental",
                            lastSync: toStore.lastSynced,
                            progress: "",
                            scanProgress: "",
                            size: scene.manifest.totalByteSize,
                            viewerScenes: toStore.viewerScenes,
                        }),
                    );

                    scene.logger = createLogger(parentSceneId, "incremental");
                    break;
                }
                case "readSize": {
                    const scene =
                        offlineWorkerState.scenes.get(parentSceneId) ??
                        (await offlineWorkerState.addScene(parentSceneId));

                    if (!scene) {
                        break;
                    }
                    const size = await scene.getUsedSize();
                    dispatch(
                        offlineActions.updateScene({
                            id: parentSceneId,
                            updates: {
                                size,
                            },
                        }),
                    );
                    break;
                }
                case "estimate": {
                    const scene =
                        offlineWorkerState.scenes.get(parentSceneId) ??
                        (await offlineWorkerState.addScene(parentSceneId));

                    if (!scene) {
                        break;
                    }

                    if (
                        offlineWorkerState.initialStorageEstimate?.quota === undefined ||
                        offlineWorkerState.initialStorageEstimate?.usage === undefined
                    ) {
                        break;
                    }

                    const availableSize = Math.max(
                        0,
                        offlineWorkerState.initialStorageEstimate.quota -
                            offlineWorkerState.initialStorageEstimate.usage,
                    );
                    const totalSize = await scene.readManifest(
                        view.offline!.manifestUrl,
                        abortController.current.signal,
                    );
                    const usedSize = await scene.getUsedSize();

                    if (totalSize === undefined) {
                        break;
                    }

                    const showWarning = totalSize - usedSize >= availableSize;
                    if (showWarning) {
                        dispatch(
                            offlineActions.setSizeWarning({
                                totalSize,
                                usedSize,
                                availableSize,
                            }),
                        );
                    } else {
                        resetAction = false;
                        dispatch(offlineActions.setAction({ action: "fullSync" }));
                    }

                    break;
                }
                case "fullSync": {
                    const scene =
                        offlineWorkerState.scenes.get(parentSceneId) ??
                        (await offlineWorkerState.addScene(parentSceneId));

                    if (!scene) {
                        break;
                    }

                    // Installed before the checks below so their failures are reported as a full
                    // sync. Otherwise they go to whatever logger the scene already carries — an
                    // incremental one, which reports nothing to the host, or none at all.
                    scene.logger = createLogger(parentSceneId, "fullSync");

                    // Procore branch: readManifest + getProcoreModelTitle.
                    // Standalone keeps the dataApi.loadScene + bookmarks path
                    // (calling those for Procore scenes errors and aborts sync).
                    let sceneTitle: string;

                    if (isProcoreBackendProvider) {
                        if (
                            (await scene.readManifest(getSceneIndexUrl(view), abortController.current.signal)) ===
                            undefined
                        ) {
                            scene.logger?.error({ message: "Failed to read the offline manifest" });
                            break;
                        }
                        sceneTitle =
                            (await getProcoreModelTitle(viewerSceneId).catch((err) => {
                                console.error(err);
                                return;
                            })) ?? viewerSceneId;
                    } else {
                        const sceneData = await dataApi.loadScene(viewerSceneId);
                        if ("error" in sceneData) {
                            scene.logger?.error({ message: "Failed to load scene data" });
                            break;
                        }
                        sceneTitle = sceneData.title;

                        await Promise.all([
                            getBookmarks({ projectId: viewerSceneId }).unwrap(),
                            user
                                ? getBookmarks({ projectId: viewerSceneId, personal: true }).unwrap()
                                : Promise.resolve(),
                            ...sceneData.objectGroups
                                .filter((group) => group.id && !group.ids)
                                .map((group) => getGroupIds({ projectId: viewerSceneId, groupId: group.id })),
                        ]);
                    }

                    const persisted = await navigator.storage.persisted();
                    if (!persisted) {
                        await navigator.storage.persist();
                    }

                    const meta = storage.get(parentSceneId);
                    const viewerScene = {
                        id: viewerSceneId,
                        name: sceneTitle,
                        lastSynced: new Date().toISOString(),
                    };

                    const toStore = {
                        id: parentSceneId,
                        name: parentSceneId,
                        lastSynced: meta?.lastSynced ?? "",
                        viewerScenes: meta
                            ? meta.viewerScenes.find((vs) => vs.id === viewerSceneId)
                                ? meta.viewerScenes.map((vs) => (vs.id === viewerSceneId ? viewerScene : vs))
                                : meta.viewerScenes.concat(viewerScene)
                            : [viewerScene],
                    };
                    storage.set(toStore);

                    dispatch(
                        offlineActions.addScene({
                            id: scene.id,
                            name: scene.id,
                            status: "incremental",
                            lastSync: toStore.lastSynced,
                            progress: "",
                            scanProgress: "",
                            size: scene.manifest.totalByteSize,
                            viewerScenes: toStore.viewerScenes,
                        }),
                    );

                    const sync = scene.sync(getSceneIndexUrl(view), abortController.current.signal);

                    if (isProcoreDesign) {
                        // Await so `action` stays set throughout the sync; otherwise
                        // the Download button flickers between SDK status phases.
                        try {
                            await sync;
                        } catch (e) {
                            // A rejected sync would otherwise skip the `action` reset
                            // below, leaving the download progress spinner stuck.
                            // Swallow it so `action` clears; surface non-abort failures
                            // via the logger so the spinner is replaced by the error
                            // banner (aborts are user-initiated pauses, not errors).
                            if (!abortController.current.signal.aborted) {
                                scene.logger?.error({ message: e instanceof Error ? e.message : String(e) });
                            }
                        }
                    }

                    break;
                }
            }

            if (resetAction) {
                dispatch(offlineActions.setAction(undefined));
            }
        }
    }, [
        action,
        dispatch,
        offlineWorkerState,
        view,
        abort,
        abortController,
        abortDeleteStaleFiles,
        createLogger,
        viewerSceneId,
        user,
        getGroupIds,
        getBookmarks,
        projectId,
        getProcoreModelTitle,
        isProcoreBackendProvider,
        isProcoreDesign,
        incrementalCachingDisabled,
        emitDownloadStatus,
    ]);
}

function useCreateLogger() {
    const {
        state: { offlineWorkerState },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const emitDownloadStatus = useEmitDownloadStatus();
    // Read via a ref so the logger can tell whether an error happened during a
    // user-initiated full sync without recreating the memoized logger (which is
    // stored on scene.logger) whenever the action changes. For Procore the
    // fullSync action stays set for the whole sync (it's awaited).
    const action = useAppSelector(selectOfflineAction);
    const actionRef = useRef(action);
    actionRef.current = action;

    /**
     * @param source Which offline work the logger reports on. Only `"fullSync"` is reported to the
     *   host; incremental caching stays in the viewer.
     */
    const createLogger = useCallback(
        (parentSceneId: string, source: "fullSync" | "incremental"): OfflineViewState["logger"] => {
            const reportToHost = source === "fullSync";

            return {
                status: (status) => {
                    if (reportToHost && isHostReportedStatus(status)) {
                        emitDownloadStatus("fullSync", { status });
                    }

                    if (status === "synchronized") {
                        const now = new Date().toISOString();
                        const scene = storage.get(parentSceneId);
                        if (scene) {
                            storage.set({
                                ...scene,
                                lastSynced: now,
                            });
                        }
                        dispatch(
                            offlineActions.updateScene({
                                id: parentSceneId,
                                updates: {
                                    status,
                                    lastSync: now,
                                    size: offlineWorkerState?.scenes.get(parentSceneId)?.manifest.totalByteSize ?? 0,
                                },
                            }),
                        );
                    } else {
                        dispatch(offlineActions.updateScene({ id: parentSceneId, updates: { status } }));
                    }
                },
                error: (error) => {
                    telemetry.trackCaughtException({
                        error,
                        source: "offline.useHandleOffline.useCreateLogger",
                        severity: "warning",
                        category: "data",
                    });

                    const msg = getOfflineDownloadErrorMessage(error.id, t);

                    if (reportToHost) {
                        emitDownloadStatus("fullSync", {
                            status: "error",
                            error: msg,
                            errorCode: getOfflineDownloadErrorCode(error.id),
                        });
                    }

                    dispatch(
                        offlineActions.updateScene({
                            id: parentSceneId,
                            updates: {
                                status: "error",
                                error: msg,
                                errorFromFullSync: actionRef.current?.action === "fullSync",
                            },
                        }),
                    );
                },
                progress: (current, max, operation) => {
                    if (operation === "download") {
                        if (current === max) {
                            dispatch(
                                offlineActions.updateScene({
                                    id: parentSceneId,
                                    updates: { progress: "100%", size: max },
                                }),
                            );
                        }

                        if (!max) {
                            dispatch(offlineActions.updateScene({ id: parentSceneId, updates: { progress: "" } }));
                        }

                        if (current !== undefined && max !== undefined) {
                            const progress = ((current / max) * 100).toFixed(2);
                            dispatch(
                                offlineActions.updateScene({ id: parentSceneId, updates: { progress, size: max } }),
                            );
                        }
                    } else {
                        if (current === max) {
                            dispatch(
                                offlineActions.updateScene({ id: parentSceneId, updates: { scanProgress: "100%" } }),
                            );
                        }

                        if (!max) {
                            dispatch(
                                offlineActions.updateScene({
                                    id: parentSceneId,
                                    updates: { scanProgress: String(current) },
                                }),
                            );
                        }

                        if (current !== undefined && max !== undefined) {
                            const scanProgress = ((current / max) * 100).toFixed(2);
                            dispatch(offlineActions.updateScene({ id: parentSceneId, updates: { scanProgress } }));
                        }
                    }
                },
            };
        },
        [dispatch, offlineWorkerState, t, emitDownloadStatus],
    );

    return createLogger;
}

/** What one status change has to report; the emitter stamps the identity fields. */
type OfflineDownloadStatusUpdate = Omit<OfflineDownloadStatusPayload, "sceneId" | "sceneVersionId" | "source">;

/**
 * Reports offline status changes to the host embedding the viewer, via the internal emitter the
 * Webviewer bridge forwards to `webviewer.eventEmitter`.
 *
 * Only the SDK's status and error callbacks are forwarded; the download progress callback is left to
 * the in-viewer indicator. Incremental caching reaches those callbacks through the same kind of
 * logger, so callers gate on the logger's `source` rather than reporting everything they hear.
 *
 * The open scene's ids are stamped here so every event carries the same identity, whichever callback
 * it came from.
 */
function useEmitDownloadStatus() {
    const {
        state: { internalEventEmitter },
    } = useExplorerGlobals();
    const sceneId = useSceneId();
    const sceneVersionId = useAppSelector(selectProcoreContext)?.sceneVersionId;

    return useCallback(
        (source: OfflineDownloadStatusPayload["source"], update: OfflineDownloadStatusUpdate) => {
            void internalEventEmitter?.emit("offlineDownloadStatus", {
                ...update,
                sceneId,
                ...(sceneVersionId ? { sceneVersionId } : {}),
                source,
            });
        },
        [internalEventEmitter, sceneId, sceneVersionId],
    );
}

function getSceneIndexUrl(view: View): URL {
    const url = view.renderState.scene?.url ? new URL(view.renderState.scene.url) : undefined;

    if (!url) {
        throw new Error("Unable to parse url for SAS key");
    }

    // Check if this is a Procore FAS URL (contains /versions/)
    // Procore FAS requires ?file= query parameter instead of pathname concatenation
    if (url.pathname.includes("/versions/")) {
        url.searchParams.set("file", "index.json");
    } else {
        // Novorender-style: append to pathname
        url.pathname += "index.json";
    }

    return url;
}

function useGetProcoreModelTitle() {
    const procoreContext = useAppSelector(selectProcoreContext);
    const [getModelRevisions] = useLazyGetModelRevisionsQuery();
    const [getModels] = useLazyGetModelsQuery();

    return useCallback(
        async (sceneId: string) => {
            const revisionsArgs = buildModelRevisionsArgs(procoreContext, sceneId);
            if (!procoreContext || !revisionsArgs) {
                return undefined;
            }

            // preferCacheValue: reuse the shared session cache instead of refetching.
            const [modelRevisionsData, modelsData] = await Promise.all([
                getModelRevisions(revisionsArgs, true).unwrap(),
                getModels(
                    {
                        companyId: procoreContext.companyId,
                        projectId: procoreContext.projectId,
                    },
                    true,
                ).unwrap(),
            ]);

            const modelRevision = modelRevisionsData?.[0];
            const model = modelRevision ? modelsData?.data.find((m) => m.id === modelRevision.bim_model_id) : undefined;

            return model?.name;
        },
        [procoreContext, getModelRevisions, getModels],
    );
}
